import { LogOutIcon } from 'lucide-react'
import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { paths } from 'src/config/paths'
import { Button } from 'src/shared/components/ui/buttons/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'src/shared/components/ui/popups/dropdown-menu'
import { useSignOut } from 'src/shared/features/auth/api/use-signout'
import ConsumerAuthDialog from 'src/shared/features/auth/components/consumer/auth-dialog'
import { useAuthenticatedConsumer } from 'src/shared/features/consumers/api/use-authenticated-consumer'
import { cn } from 'src/shared/utils/cn'

import { useMyUserData } from '../../api/accounts/use-my-user'
import ProfileAvatar from '../profile-photos/profile-avatar'

import AuthModal from './modals/auth'

const Container = ({
  children,
  authDialogOpen,
  setAuthDialogOpen,
  initialView,
}: {
  children: React.ReactNode
  authDialogOpen: boolean
  setAuthDialogOpen: (open: boolean) => void
  initialView?: 'login' | 'signup'
}) => {
  return (
    <>
      <div className="flex items-center gap-3">
        {children}
        <div className="absolute">
          <ConsumerAuthDialog open={authDialogOpen} onOpenChange={setAuthDialogOpen} initialView={initialView} />
        </div>
      </div>
    </>
  )
}

const HeaderAuthIndicator = ({ variant = 'marketplace' }: { variant?: 'marketplace' | 'home' }) => {
  const navigate = useNavigate()
  const { data: consumer, isLoading: consumerLoading } = useAuthenticatedConsumer()
  const { data: user, isLoading: userLoading } = useMyUserData()
  const signOut = useSignOut({ isConsumer: true })
  const [authDialogOpen, setAuthDialogOpen] = useState(false)
  const [initialView, setInitialView] = useState<'login' | 'signup'>('login')
  const [showModal, setShowModal] = useState(false)
  const [authModalFlow, setAuthModalFlow] = useState<'signup' | 'login'>('login')

  const handleClientLoginClick = (signup = false) => {
    setInitialView(signup ? 'signup' : 'login')
    setShowModal(true)
    setAuthModalFlow(signup ? 'signup' : 'login')
  }

  const handleLogout = async () => {
    await signOut.mutateAsync()
    navigate(paths.marketplace.getHref())
  }

  if (consumerLoading || userLoading) {
    return (
      <Container authDialogOpen={authDialogOpen} setAuthDialogOpen={setAuthDialogOpen} initialView={initialView}>
        {/* <div className="size-11 min-w-11 animate-pulse rounded-full bg-muted" /> */}
        <Button
          onClick={() => {}}
          variant="accent"
          className={cn(
            'min-h-0 w-max rounded-full border-border bg-white px-4 text-[13px] md:min-h-12 md:px-8 md:text-base',
            variant === 'home' && 'border-none bg-transparent px-2',
          )}
        >
          Login
        </Button>
        <Button
          variant="accent"
          onClick={() => {}}
          className={cn(
            'min-h-0 w-max rounded-full border-secondaryInverse bg-secondaryInverse px-4 text-[13px] md:min-h-12 md:px-8 md:text-base',
            variant === 'home' && 'border-primary bg-primary text-white',
          )}
        >
          Sign up
        </Button>
      </Container>
    )
  }

  if (consumer) {
    return (
      <Container authDialogOpen={authDialogOpen} setAuthDialogOpen={setAuthDialogOpen} initialView={initialView}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex cursor-pointer flex-row items-center gap-3 rounded-full border border-border bg-white p-2 text-[14px]">
              <p className="ml-3 flex size-full items-center justify-center text-sm font-semibold text-primary">
                {consumer.firstName}
              </p>
              <ProfileAvatar userId={-1} size={35} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="h-12 flex-col items-center justify-center">
            <DropdownMenuItem className="size-full text-left">
              <button
                type="button"
                onClick={handleLogout}
                className="flex size-full flex-row items-center justify-between"
              >
                <p className="text-sm font-medium text-primary">Logout</p>
                <LogOutIcon className="ml-2 size-4 text-primary" />
              </button>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </Container>
    )
  }

  if (user) {
    return (
      <Container authDialogOpen={authDialogOpen} setAuthDialogOpen={setAuthDialogOpen} initialView={initialView}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              onClick={() => navigate(paths.users.profile.getHref(user.username))}
              className="flex cursor-pointer flex-row items-center gap-3 rounded-full border border-border bg-white p-2 text-[14px]"
            >
              <p className="ml-3 flex size-full items-center justify-center text-sm font-semibold text-primary">
                {user.firstName}
              </p>
              <ProfileAvatar userId={-1} size={35} />
            </button>
          </DropdownMenuTrigger>
        </DropdownMenu>
      </Container>
    )
  }

  return (
    <>
      <Container authDialogOpen={authDialogOpen} setAuthDialogOpen={setAuthDialogOpen} initialView={initialView}>
        <Button
          onClick={() => handleClientLoginClick(false)}
          variant="accent"
          className={cn(
            'min-h-0 w-max rounded-full border-border bg-white px-4 text-[13px] md:min-h-12 md:px-8 md:text-base',
            variant === 'home' && 'border-none bg-transparent px-2',
          )}
        >
          Login
        </Button>
        <Button
          variant="accent"
          onClick={() => handleClientLoginClick(true)}
          className={cn(
            'min-h-0 w-max rounded-full border-secondaryInverse bg-secondaryInverse px-4 text-[13px] md:min-h-12 md:px-8 md:text-base',
            variant === 'home' && 'border-primary bg-primary text-white',
          )}
        >
          Sign up
        </Button>
      </Container>
      <AuthModal flow={authModalFlow} open={showModal} onClose={() => setShowModal(false)} />
    </>
  )
}

export default HeaderAuthIndicator
